import type { FC } from 'react';
import c from 'classnames';
import { PapiSellerDiscount } from '@wix/da-papi-types';
import {
  DATE_FORMATS,
  formatDate,
  formatTime,
  TIME_FORMATS,
} from '@wix/da-i18next/pkg/time/functions';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
//   studio.discountModal.form.appliesToCategoryInput.option.*
//   studio.discountModal.form.appliesToProductInput.option.*
//   studio.discountModal.form.audienceInput.option.*
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  BiData,
  DuplicateDiscountClickBiEvent,
  EndDiscountClickBiEvent,
} from '@wix/da-bi/pkg/events';
import MoreButton from '../../DiscountRow/MoreButton';
import {
  useDeleteDiscount,
  useDiscountRowActions,
  useEndDiscount,
} from '../../SellerDiscountModal/hooks';
import SellerDiscountModal from '../../SellerDiscountModal';
import { useSelector } from 'react-redux';
import { getActiveSection } from '../../../../../selectors/studio';
import { isDiscountActive } from '../../helpers';
import { getAppliesToBabelKeyNew } from '../../SellerDiscountModal/_partials/DiscountPreviewCard/helpers';
import { StudioSection } from '../../../../../types/store';

import s from './DiscountRowMobileNew.scss';

interface Props {
  discount: PapiSellerDiscount;
  className?: string;
  statusSection?: StudioSection;
}

const DiscountRowMobileNew: FC<Props> = ({ discount, className, statusSection }) => {
  const { t, i18n } = useTranslation();
  const logBiEvent = useBiLogger();
  const activeSection = useSelector(getActiveSection);
  const sectionToUse = statusSection ?? activeSection;
  const isActive = isDiscountActive(discount);
  const { isModalOpen, modalMode, openEditModal, openDuplicateModal, closeModal } =
    useDiscountRowActions(discount.discountId);
  const { deleteDiscount } = useDeleteDiscount();
  const handleDelete = () => {
    deleteDiscount(discount.discountId);
  };
  const { endDiscount } = useEndDiscount();
  const handleEndDiscount = () => {
    logBiEvent(
      BiData<EndDiscountClickBiEvent>({
        evid: 453,
        campaignid: discount.discountId,
      })
    );
    endDiscount(discount.discountId);
  };
  const handleDuplicateDiscount = () => {
    logBiEvent(
      BiData<DuplicateDiscountClickBiEvent>({
        evid: 456,
        campaignid: discount.discountId,
      })
    );
    openDuplicateModal();
  };

  const prettyStartDate = formatDate(discount.startDate, DATE_FORMATS.Date.Short, i18n.language);
  const prettyStartTime = formatTime(discount.startDate, TIME_FORMATS.HourMinute, i18n.language)
    .replace(/:00/, '')
    .toLowerCase();

  const prettyEndDate = formatDate(discount.endDate, DATE_FORMATS.Date.Short, i18n.language);
  const prettyEndTime = formatTime(discount.endDate, TIME_FORMATS.HourMinute, i18n.language)
    .replace(/:00/, '')
    .toLowerCase();

  // discount.appliesto from API is the final value (e.g., 'any_tier', 'any_shop_product')
  const appliesToString = t(getAppliesToBabelKeyNew(discount.appliesto));
  const audienceString = t(`studio.discountModal.form.audienceInput.option.${discount.audience}`);

  return (
    <div className={c(s['root'], className)}>
      <div className={s['content']}>
        <div className={s['header']}>
          <div className={s['name']}>{discount.name}</div>
          <MoreButton
            discount={discount}
            isActive={isActive}
            activeSection={sectionToUse}
            onEditClick={openEditModal}
            onDuplicateClick={handleDuplicateDiscount}
            onDeleteClick={handleDelete}
            onEndDiscountClick={handleEndDiscount}
          />
        </div>
        <div className={s['discount-info']}>
          {t('studio.discountRow.discountOff', {
            percent: discount.amount?.percent,
            productType: appliesToString,
          })}
        </div>
        <div className={s['dates']}>
          <span className={s['date-value']}>{prettyStartDate},</span>
          <span className={s['time-value']}>{prettyStartTime}</span>
          <span className={s['date-value']}>{prettyEndDate},</span>
          <span className={s['time-value']}>{prettyEndTime}</span>
        </div>
        <div className={s['audience']}>{audienceString}</div>
      </div>
      {isModalOpen && (
        <SellerDiscountModal discount={discount} mode={modalMode} onRequestClose={closeModal} />
      )}
    </div>
  );
};
DiscountRowMobileNew.displayName = 'DiscountRowMobileNew';

export default DiscountRowMobileNew;
